<script setup>
import { ldJson } from '@/lib/jsonLd';
import { Head, Link } from '@inertiajs/vue3';

defineProps({
    domain: { type: Object, default: null },
    region: { type: String, required: true },
    regionSlug: { type: String, required: true },
    cities: { type: Array, default: () => [] },
    seoTitle: String,
    metaDescription: String,
    canonicalUrl: String,
    h1: String,
    intro: String,
    jsonLd: { type: [Object, Array], default: null },
});
</script>

<template>
    <Head :title="seoTitle">
        <meta name="description" :content="metaDescription || ''" />
        <component
            :is="'script'"
            v-if="jsonLd"
            type="application/ld+json"
            v-html="ldJson(jsonLd)"
        />
    </Head>

    <main class="standorte-hub">
        <header class="standorte-hub__header">
            <p class="standorte-hub__eyebrow">Standorte / {{ region }}</p>
            <h1>{{ h1 }}</h1>
            <p class="standorte-hub__intro">{{ intro }}</p>
        </header>

        <section class="standorte-hub__grid">
            <article
                v-for="c in cities"
                :key="c.slug"
                class="standorte-hub__card"
            >
                <h2>{{ c.name }}</h2>
                <p v-if="c.hero" class="standorte-hub__hero">{{ c.hero }}</p>
                <ul class="standorte-hub__links">
                    <li>
                        <Link :href="c.wp_url">Wärmepumpe in {{ c.name }}</Link>
                    </li>
                    <li>
                        <Link :href="c.pv_url">Photovoltaik in {{ c.name }}</Link>
                    </li>
                </ul>
            </article>
        </section>

        <footer class="standorte-hub__footer">
            <p>
                Bis zu drei Angebote regionaler SHK- und PV-Fachbetriebe -
                unverbindlich, kostenlos, ohne Verkaufsdruck. Jede Stadt-Seite zeigt die
                lokal zuständige Innung, die KfW-Förderhöhe für die Region und die
                durchschnittliche Amortisationszeit.
            </p>
        </footer>
    </main>
</template>

<style scoped>
.standorte-hub {
    max-width: 1100px;
    margin: 0 auto;
    padding: 40px 24px 80px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #0f172a;
}
.standorte-hub__eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 12px;
    color: #64748b;
    margin: 0 0 12px;
}
.standorte-hub__header h1 {
    font-size: 32px;
    line-height: 1.2;
    margin: 0 0 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
}
.standorte-hub__intro {
    font-size: 16px;
    line-height: 1.65;
    color: #334155;
    max-width: 760px;
    margin: 0 0 40px;
}
.standorte-hub__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
}
.standorte-hub__card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    background: #ffffff;
    transition: border-color 0.15s ease, transform 0.15s ease;
}
.standorte-hub__card:hover {
    border-color: #fb923c;
}
.standorte-hub__card h2 {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
}
.standorte-hub__hero {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 12px;
    line-height: 1.5;
}
.standorte-hub__links {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.standorte-hub__links a {
    color: #ea580c;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
}
.standorte-hub__links a:hover {
    text-decoration: underline;
}
.standorte-hub__footer {
    margin-top: 48px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    font-size: 14px;
    color: #475569;
    line-height: 1.6;
}
</style>
